import { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Users, Plus, Trash2, User, Building2, ChevronRight, Edit, ChevronDown, ChevronUp, UserPlus } from 'lucide-react';
import { z } from 'zod';
import { formatPhoneNumber } from '@/utils/formatters';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';

// US States for dropdown
// PayArc-compatible title options
const TITLE_OPTIONS = [
  { value: "CEO", label: "CEO" },
  { value: "CFO", label: "CFO" },
  { value: "Director", label: "Director" },
  { value: "Owner", label: "Owner" },
  { value: "Partner", label: "Partner" },
  { value: "President", label: "President" },
  { value: "Secretary", label: "Secretary" },
  { value: "Treasurer", label: "Treasurer" },
  { value: "Vice President", label: "Vice President" },
  { value: "Other", label: "Other" },
];

const states = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const formatSSN = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
};

const createOwnerSchema = (shouldShowField: (fieldPath: string) => boolean, isFieldRequired: (fieldPath: string) => boolean) => {
  const ownerFields: any = {};

  if (shouldShowField('beneficialOwners.firstName')) {
    ownerFields.firstName = isFieldRequired('beneficialOwners.firstName')
      ? z.string().min(1, 'First name is required')
      : z.string().optional();
  }

  if (shouldShowField('beneficialOwners.lastName')) {
    ownerFields.lastName = isFieldRequired('beneficialOwners.lastName')
      ? z.string().min(1, 'Last name is required')
      : z.string().optional();
  }

  if (shouldShowField('beneficialOwners.title')) {
    ownerFields.title = isFieldRequired('beneficialOwners.title')
      ? z.string().min(1, 'Title is required')
      : z.string().optional();
  }

  if (shouldShowField('beneficialOwners.ownershipPercentage')) {
    ownerFields.ownershipPercentage = isFieldRequired('beneficialOwners.ownershipPercentage')
      ? z.string()
          .min(1, 'Ownership percentage is required')
          .refine((val) => {
            const num = parseFloat(val);
            return !isNaN(num) && num > 0 && num <= 100;
          }, 'Must be between 1% and 100%')
      : z.string().optional();
  }

  if (shouldShowField('beneficialOwners.email')) {
    ownerFields.email = isFieldRequired('beneficialOwners.email')
      ? z.string().email('Invalid email address')
      : z.string().email('Invalid email address').optional();
  }

  if (shouldShowField('beneficialOwners.phone')) {
    ownerFields.phone = isFieldRequired('beneficialOwners.phone')
      ? z.string().min(14, 'Phone number must be at least 10 digits')
      : z.string().optional();
  }

  if (shouldShowField('beneficialOwners.homeAddress')) {
    const addressSchema = {
      street: isFieldRequired('beneficialOwners.homeAddress.street')
        ? z.string().min(1, 'Street address is required')
        : z.string().optional(),
      city: isFieldRequired('beneficialOwners.homeAddress.city')
        ? z.string().min(1, 'City is required')
        : z.string().optional(),
      state: isFieldRequired('beneficialOwners.homeAddress.state')
        ? z.string().min(2, 'State is required')
        : z.string().optional(),
      zipCode: isFieldRequired('beneficialOwners.homeAddress.zipCode')
        ? z.string().min(5, 'ZIP code is required')
        : z.string().optional(),
      fullAddress: isFieldRequired('beneficialOwners.homeAddress.fullAddress')
        ? z.string().min(1, 'Full address is required')
        : z.string().optional(),
    };
    ownerFields.homeAddress = z.object(addressSchema);
  }

  // Add checkbox field for using business address
  ownerFields.sameAsBusinessAddress = z.boolean().optional();

  // Legacy fields for backwards compatibility
  if (shouldShowField('beneficialOwners.address')) {
    ownerFields.address = z.string().optional();
  }
  if (shouldShowField('beneficialOwners.city')) {
    ownerFields.city = z.string().optional();
  }
  if (shouldShowField('beneficialOwners.state')) {
    ownerFields.state = z.string().optional();
  }
  if (shouldShowField('beneficialOwners.zipCode')) {
    ownerFields.zipCode = z.string().optional();
  }

  if (shouldShowField('beneficialOwners.ssn')) {
    ownerFields.ssn = isFieldRequired('beneficialOwners.ssn')
      ? z.string()
          .min(11, 'SSN must be 9 digits')
          .regex(/^\d{3}-\d{2}-\d{4}$/, 'SSN must be in format XXX-XX-XXXX')
      : z.string().optional();
  }

  if (shouldShowField('beneficialOwners.dateOfBirth')) {
    ownerFields.dateOfBirth = isFieldRequired('beneficialOwners.dateOfBirth')
      ? z.string().min(1, 'Date of birth is required')
      : z.string().optional();
  }

  if (shouldShowField('beneficialOwners.driverLicense')) {
    ownerFields.driverLicense = isFieldRequired('beneficialOwners.driverLicense')
      ? z.string().min(1, 'Driver\'s license is required')
      : z.string().optional();
  }

  // Use passthrough() to preserve fields not explicitly defined in the schema
  // This prevents data loss when fields are conditionally hidden via shouldShowField()
  return z.object(ownerFields).passthrough();
};

const createBeneficialOwnerSchema = (shouldShowField: (fieldPath: string) => boolean, isFieldRequired: (fieldPath: string) => boolean) => {
  const ownerSchema = createOwnerSchema(shouldShowField, isFieldRequired);

  return z.object({
    owners: z.array(ownerSchema)
      .min(1, 'At least one beneficial owner is required')
      .max(4, 'Maximum of 4 beneficial owners allowed')
      .refine((owners) => {
        if (!shouldShowField('beneficialOwners.ownershipPercentage')) return true;
        const total = owners.reduce((sum, owner) => sum + parseFloat(owner.ownershipPercentage || '0'), 0);
        return total >= 75 && total <= 100.01;
      }, 'Ownership percentages must total at least 75% and not exceed 100%'),
  }).passthrough();
};

interface BeneficialOwnerStepProps {
  onSubmit: (data: any) => void;
  onPrevious?: () => void;
  initialData?: any;
  shouldShowField?: (fieldPath: string) => boolean;
  isFieldRequired?: (fieldPath: string) => boolean;
  getFieldDefaultValue?: (fieldPath: string, includeWhenVisible?: boolean) => string | undefined;
}


export function BeneficialOwnerStep({ 
  onSubmit, 
  onPrevious, 
  initialData,
  shouldShowField = () => true,
  isFieldRequired = () => true,
  getFieldDefaultValue
}: BeneficialOwnerStepProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedOwners, setExpandedOwners] = useState<Set<number>>(new Set());
  const applicationType: 'individual' | 'corporation' | undefined = initialData?.applicationType;

  // Create dynamic schema based on conditional requirements
  const beneficialOwnerSchema = useMemo(() => {
    return createBeneficialOwnerSchema(shouldShowField, isFieldRequired);
  }, [shouldShowField, isFieldRequired]);

  // Pre-fill the primary owner from personal/corporation info
  const getPrimaryOwnerData = () => {
    const personalInfo = initialData?.personalInfo || {};
    const corporationInfo = initialData?.corporationInfo || {};
    const businessAddress = corporationInfo?.businessAddress || {};

    return {
      firstName: personalInfo.firstName || '',
      lastName: personalInfo.lastName || '',
      title: applicationType === 'individual' ? 'Owner' : 'CEO',
      ownershipPercentage: '100',
      email: personalInfo.email || '',
      phone: personalInfo.phone || '',
      sameAsBusinessAddress: true, // Default to using business address
      homeAddress: {
        street: businessAddress.street || '',
        city: businessAddress.city || '',
        state: businessAddress.state || '',
        zipCode: businessAddress.zipCode || '',
        fullAddress: businessAddress.fullAddress || '',
      },
      // Legacy fields for backwards compatibility
      address: businessAddress.street || '',
      city: businessAddress.city || '',
      state: businessAddress.state || '',
      zipCode: businessAddress.zipCode || '',
      ssn: '', // Will be filled in during this step
      dateOfBirth: '', // Will be filled in during this step
      driverLicense: '', // Will be filled in during this step
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
    reset,
  } = useForm({
    resolver: zodResolver(beneficialOwnerSchema),
    defaultValues: {
      owners: initialData?.beneficialOwners || [getPrimaryOwnerData()],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'owners',
  });

  const owners = watch('owners');
  const totalOwnership = owners.reduce((sum, owner) => sum + parseFloat(owner.ownershipPercentage || '0'), 0);

  // Auto-expand first owner if they need to fill in required information
  useEffect(() => {
    if (fields.length > 0) {
      const firstOwner = watch('owners.0');
      const needsInfo = !firstOwner?.ssn || !firstOwner?.dateOfBirth || !firstOwner?.driverLicense || !firstOwner?.ownershipPercentage;
      
      if (needsInfo && !expandedOwners.has(0)) {
        setExpandedOwners(prev => new Set([...prev, 0]));
      }
    }
  }, [fields.length, watch]);

  const handlePhoneChange = (index: number, value: string) => {
    const cleanedValue = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const truncatedValue = cleanedValue.slice(0, 10);
    
    // Format the phone number
    let formattedValue = '';
    if (truncatedValue.length > 0) {
      if (truncatedValue.length <= 3) {
        formattedValue = truncatedValue;
      } else if (truncatedValue.length <= 6) {
        formattedValue = `(${truncatedValue.slice(0, 3)}) ${truncatedValue.slice(3)}`;
      } else {
        formattedValue = `(${truncatedValue.slice(0, 3)}) ${truncatedValue.slice(3, 6)}-${truncatedValue.slice(6)}`;
      }
    }
    
    setValue(`owners.${index}.phone`, formattedValue, { shouldValidate: false });
  };

  const handleSSNChange = (index: number, value: string) => {
    const formatted = formatSSN(value);
    setValue(`owners.${index}.ssn`, formatted, { shouldValidate: false });
  };

  const handleHomeAddressChange = (index: number, address: any) => {
    setValue(`owners.${index}.homeAddress`, address, {
      shouldValidate: true,
    });
    // Also update legacy fields for backwards compatibility
    setValue(`owners.${index}.address`, address.street || '', { shouldValidate: false });
    setValue(`owners.${index}.city`, address.city || '', { shouldValidate: false });
    setValue(`owners.${index}.state`, address.state || '', { shouldValidate: false });
    setValue(`owners.${index}.zipCode`, address.zipCode || '', { shouldValidate: false });
  };

  const handleSameAsBusinessAddressChange = (index: number, checked: boolean) => {
    setValue(`owners.${index}.sameAsBusinessAddress`, checked, { shouldValidate: false });
    
    if (checked) {
      const corporationInfo = initialData?.corporationInfo || {};
      const businessAddress = corporationInfo?.businessAddress || {};
      
      setValue(`owners.${index}.homeAddress`, {
        street: businessAddress.street || '',
        city: businessAddress.city || '',
        state: businessAddress.state || '',
        zipCode: businessAddress.zipCode || '',
        fullAddress: businessAddress.fullAddress || '',
      }, { shouldValidate: true });
      
      // Update legacy fields too
      setValue(`owners.${index}.address`, businessAddress.street || '', { shouldValidate: false });
      setValue(`owners.${index}.city`, businessAddress.city || '', { shouldValidate: false });
      setValue(`owners.${index}.state`, businessAddress.state || '', { shouldValidate: false });
      setValue(`owners.${index}.zipCode`, businessAddress.zipCode || '', { shouldValidate: false });
    } else {
      // Clear address when unchecked
      setValue(`owners.${index}.homeAddress`, {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        fullAddress: '',
      }, { shouldValidate: true });
      
      // Clear legacy fields too
      setValue(`owners.${index}.address`, '', { shouldValidate: false });
      setValue(`owners.${index}.city`, '', { shouldValidate: false });
      setValue(`owners.${index}.state`, '', { shouldValidate: false });
      setValue(`owners.${index}.zipCode`, '', { shouldValidate: false });
    }
  };


  const onFormSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      setServerError(null);
      
      // For individual applications, ensure 100% ownership is sent
      if (applicationType === 'individual') {
        const normalizedOwners = owners.map((owner, idx) => ({
          ...owner,
          ownershipPercentage: idx === 0 ? '100' : owner.ownershipPercentage,
        }));
        await onSubmit({ beneficialOwners: normalizedOwners });
      } else {
        // For corporation applications, submit directly
        await onSubmit({ beneficialOwners: owners });
      }
    } catch (error) {
      console.error('Submission error:', error);
      setServerError('An error occurred while saving your information');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleOwnerExpansion = (index: number) => {
    const newExpanded = new Set(expandedOwners);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedOwners(newExpanded);
  };

  const addOwner = () => {
    if (fields.length < 4) {
      const newIndex = fields.length;
      const remaining = Math.max(0, 100 - totalOwnership);
      append({
        firstName: '',
        lastName: '',
        title: '',
        ownershipPercentage: remaining > 0 ? String(remaining) : '',
        email: '',
        phone: '',
        sameAsBusinessAddress: false,
        homeAddress: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          fullAddress: '',
        },
        // Legacy fields for backwards compatibility
        address: '',
        city: '',
        state: '',
        zipCode: '',
        ssn: '',
        dateOfBirth: '',
        driverLicense: '',
      });
      // Auto-expand the newly added owner
      setExpandedOwners(prev => new Set([...prev, newIndex]));
    }
  };

  // Function to handle ownership percentage changes with automatic adjustment
  const handleOwnershipPercentageChange = (index: number, value: string) => {
    // Allow empty value
    if (value === '') {
      setValue(`owners.${index}.ownershipPercentage`, value, { shouldValidate: true });
      return;
    }

    // Parse and clamp to 0-100
    let newPercentage = parseFloat(value) || 0;
    newPercentage = Math.min(100, Math.max(0, newPercentage));
    const clampedValue = newPercentage.toString();

    // Update the current owner's percentage with clamped value
    setValue(`owners.${index}.ownershipPercentage`, clampedValue, { shouldValidate: true });

    // If this is not the first owner, adjust the first owner's percentage automatically
    if (index !== 0 && newPercentage > 0) {
      const currentOwners = watch('owners');

      // Calculate the total percentage from all non-first owners
      let totalFromOthers = 0;
      for (let i = 1; i < currentOwners.length; i++) {
        if (i === index) {
          totalFromOthers += newPercentage; // Use the new value for current owner
        } else {
          totalFromOthers += parseFloat(currentOwners[i]?.ownershipPercentage || '0');
        }
      }

      // Adjust the first owner's percentage to maintain 100% total
      const newFirstOwnerPercentage = Math.max(0, 100 - totalFromOthers);
      setValue(`owners.0.ownershipPercentage`, String(newFirstOwnerPercentage), { shouldValidate: true });
    }
  };

  const removeOwner = (index: number) => {
    // Get the current owners before removal
    const currentOwners = watch('owners');

    remove(index);

    // Update expanded state to account for removed owner
    const newExpanded = new Set<number>();
    expandedOwners.forEach(expandedIndex => {
      if (expandedIndex < index) {
        newExpanded.add(expandedIndex);
      } else if (expandedIndex > index) {
        newExpanded.add(expandedIndex - 1);
      }
    });
    setExpandedOwners(newExpanded);

    // After removing an owner, recalculate the first owner's percentage
    // Wait for the next tick to ensure the form state has updated
    setTimeout(() => {
      const updatedOwners = watch('owners');

      // Calculate total from all non-first owners
      let totalFromOthers = 0;
      for (let i = 1; i < updatedOwners.length; i++) {
        totalFromOthers += parseFloat(updatedOwners[i]?.ownershipPercentage || '0');
      }

      // Adjust the first owner's percentage to maintain 100% total
      const newFirstOwnerPercentage = Math.max(0, 100 - totalFromOthers);
      setValue(`owners.0.ownershipPercentage`, String(newFirstOwnerPercentage), { shouldValidate: true });
    }, 0);
  };


  // Render form view
  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Beneficial Ownership Information</h2>
            <p className="text-sm text-gray-600 mt-1">Complete the ownership details for all beneficial owners</p>
          </div>
        </CardTitle>
        <CardDescription className="text-base leading-relaxed text-gray-700 mt-4">
          Please provide personal information including Social Security Number, Date of Birth, and Driver's License for identity verification. 
          {applicationType === 'corporation' && (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <strong className="text-amber-800">Corporation Requirements:</strong> List all individuals who own 25% or more of the business. Total ownership should be at least 75% (recommended 100%).
            </div>
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {serverError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}

        {/* Ownership Total - Show when needed */}
        {(applicationType === 'corporation' || applicationType === 'individual') && (
          <div className={`p-4 rounded-lg border ${
            totalOwnership >= 100 
              ? 'bg-green-50 border-green-200' 
              : totalOwnership >= 75 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${totalOwnership >= 100 ? 'bg-green-500' : totalOwnership >= 75 ? 'bg-amber-500' : 'bg-red-500'}`}></div>
              <p className="text-sm font-medium">
                Total Ownership: <span className={totalOwnership >= 100 ? 'text-green-700' : totalOwnership >= 75 ? 'text-amber-700' : 'text-red-700'}>
                  {totalOwnership.toFixed(2)}%
                </span>
              </p>
            </div>
            {totalOwnership < 75 && (
              <p className="text-xs text-red-600 mt-1">Total ownership must be at least 75%</p>
            )}
            {totalOwnership >= 75 && totalOwnership < 100 && (
              <p className="text-xs text-amber-700 mt-1">Looks good. You can optionally reach 100%.</p>
            )}
          </div>
        )}

        {/* Beneficial Owners */}
        {fields.map((field, index) => {
          const isExpanded = expandedOwners.has(index);
          const ownerData = watch(`owners.${index}`);
          
          return (
            <Card key={field.id} className="border-2 border-gray-100 hover:border-blue-200 transition-colors overflow-hidden">
              {/* Collapsible Header */}
              <div 
                className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 cursor-pointer hover:from-blue-50 hover:to-indigo-50 transition-colors"
                onClick={() => toggleOwnerExpansion(index)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      index === 0 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {index === 0 ? 'Beneficial Owner 1' : `Beneficial Owner ${index + 1}`}
                        </h3>
                        {index === 0 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                            Primary
                          </span>
                        )}
                      </div>
                      {/* Show summary when collapsed */}
                      {!isExpanded && (
                        <div className="text-sm text-gray-600 mt-1">
                          {ownerData?.firstName && ownerData?.lastName ? (
                            <span>
                              {ownerData.firstName} {ownerData.lastName}
                              {ownerData.ownershipPercentage && ` • ${ownerData.ownershipPercentage}% ownership`}
                              {ownerData.title && ` • ${ownerData.title}`}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">Click to add details</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Delete button */}
                    {applicationType === 'corporation' && fields.length > 1 && index !== 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeOwner(index);
                        }}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full p-2 h-8 w-8"
                        title="Remove this beneficial owner"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Expand/Collapse button */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="rounded-full p-2 h-8 w-8 hover:bg-gray-200"
                      title={isExpanded ? "Collapse" : "Expand"}
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Collapsible Content */}
              {isExpanded && (
                <div className="p-6">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name Fields - Nice display for first owner */}
              {index === 0 ? (
                <div className="md:col-span-2 mb-4">
                  <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-blue-600 uppercase tracking-wide">Primary Applicant</div>
                        <div className="text-xl font-semibold text-gray-900">
                          {watch(`owners.${index}.firstName`)} {watch(`owners.${index}.lastName`)}
                        </div>
                        <div className="text-sm text-blue-700">
                          {watch(`owners.${index}.title`) || 'Owner'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Hidden inputs for form validation */}
                  <input type="hidden" {...register(`owners.${index}.firstName`)} />
                  <input type="hidden" {...register(`owners.${index}.lastName`)} />
                </div>
              ) : (
                <>
                  {shouldShowField('beneficialOwners.firstName') && (
                    <div className="space-y-2">
                      <Label htmlFor={`owners.${index}.firstName`}>
                        First Name {isFieldRequired('beneficialOwners.firstName') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id={`owners.${index}.firstName`}
                        {...register(`owners.${index}.firstName`)}
                        placeholder="John"
                      />
                      {errors.owners?.[index]?.firstName && (
                        <p className="text-sm text-red-600">{errors.owners[index]?.firstName?.message}</p>
                      )}
                    </div>
                  )}

                  {shouldShowField('beneficialOwners.lastName') && (
                    <div className="space-y-2">
                      <Label htmlFor={`owners.${index}.lastName`}>
                        Last Name {isFieldRequired('beneficialOwners.lastName') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id={`owners.${index}.lastName`}
                        {...register(`owners.${index}.lastName`)}
                        placeholder="Doe"
                      />
                      {errors.owners?.[index]?.lastName && (
                        <p className="text-sm text-red-600">{errors.owners[index]?.lastName?.message}</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Title - Show as display for first owner, editable for others */}
              {shouldShowField('beneficialOwners.title') && (
                <>
                  {index === 0 ? (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Title</Label>
                      <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="text-base font-medium text-gray-900">
                          {watch(`owners.${index}.title`) || (applicationType === 'individual' ? 'Owner' : 'CEO')}
                        </div>
                      </div>
                      {/* Hidden input for form validation */}
                      <input type="hidden" {...register(`owners.${index}.title`)} />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor={`owners.${index}.title`}>
                        Title {isFieldRequired('beneficialOwners.title') && <span className="text-red-500">*</span>}
                      </Label>
                      <Select
                        onValueChange={(value) => setValue(`owners.${index}.title`, value)}
                        defaultValue={watch(`owners.${index}.title`)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select title" />
                        </SelectTrigger>
                        <SelectContent>
                          {TITLE_OPTIONS.map((title) => (
                            <SelectItem key={title.value} value={title.value}>
                              {title.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.owners?.[index]?.title && (
                        <p className="text-sm text-red-600">{errors.owners[index]?.title?.message}</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Ownership Percentage - Conditionally rendered based on field settings */}
              {shouldShowField('beneficialOwners.ownershipPercentage') && (
                <div className="space-y-2">
                  <Label htmlFor={`owners.${index}.ownershipPercentage`}>
                    Ownership Percentage {isFieldRequired('beneficialOwners.ownershipPercentage') && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id={`owners.${index}.ownershipPercentage`}
                    value={watch(`owners.${index}.ownershipPercentage`) || ''}
                    onChange={(e) => handleOwnershipPercentageChange(index, e.target.value)}
                    placeholder={applicationType === 'individual' && index === 0 ? "100" : "25"}
                    type="number"
                    min="1"
                    max="100"
                    step="0.01"
                  />
                  {errors.owners?.[index]?.ownershipPercentage && (
                    <p className="text-sm text-red-600">{errors.owners[index]?.ownershipPercentage?.message}</p>
                  )}
                </div>
              )}

              {/* Contact Info - Display for first owner, editable for others */}
              {shouldShowField('beneficialOwners.email') && (
                <>
                  {index === 0 ? (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Email</Label>
                      <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="text-base text-gray-900">
                          {watch(`owners.${index}.email`) || 'Not provided'}
                        </div>
                      </div>
                      {/* Hidden input for form validation */}
                      <input type="hidden" {...register(`owners.${index}.email`)} />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor={`owners.${index}.email`}>
                        Email {isFieldRequired('beneficialOwners.email') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id={`owners.${index}.email`}
                        {...register(`owners.${index}.email`)}
                        type="email"
                        placeholder="john@example.com"
                      />
                      {errors.owners?.[index]?.email && (
                        <p className="text-sm text-red-600">{errors.owners[index]?.email?.message}</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {shouldShowField('beneficialOwners.phone') && (
                <>
                  {index === 0 ? (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Phone</Label>
                      <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="text-base text-gray-900">
                          {watch(`owners.${index}.phone`) || 'Not provided'}
                        </div>
                      </div>
                      {/* Hidden input for form validation */}
                      <input type="hidden" {...register(`owners.${index}.phone`)} />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor={`owners.${index}.phone`}>
                        Phone {isFieldRequired('beneficialOwners.phone') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id={`owners.${index}.phone`}
                        value={watch(`owners.${index}.phone`)}
                        onChange={(e) => handlePhoneChange(index, e.target.value)}
                        placeholder="(123) 456-7890"
                      />
                      {errors.owners?.[index]?.phone && (
                        <p className="text-sm text-red-600">{errors.owners[index]?.phone?.message}</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Personal Info - Conditionally rendered based on field settings */}
              {shouldShowField('beneficialOwners.dateOfBirth') && (
                <div className="space-y-2">
                  <Label htmlFor={`owners.${index}.dateOfBirth`}>
                    Date of Birth {isFieldRequired('beneficialOwners.dateOfBirth') && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id={`owners.${index}.dateOfBirth`}
                    {...register(`owners.${index}.dateOfBirth`)}
                    type="date"
                  />
                  {errors.owners?.[index]?.dateOfBirth && (
                    <p className="text-sm text-red-600">{errors.owners[index]?.dateOfBirth?.message}</p>
                  )}
                </div>
              )}

              {shouldShowField('beneficialOwners.ssn') && (
                <div className="space-y-2">
                  <Label htmlFor={`owners.${index}.ssn`}>
                    SSN {isFieldRequired('beneficialOwners.ssn') && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id={`owners.${index}.ssn`}
                    value={watch(`owners.${index}.ssn`)}
                    onChange={(e) => handleSSNChange(index, e.target.value)}
                    placeholder="123-45-6789"
                    maxLength={11}
                  />
                  {errors.owners?.[index]?.ssn && (
                    <p className="text-sm text-red-600">{errors.owners[index]?.ssn?.message}</p>
                  )}
                </div>
              )}

              {shouldShowField('beneficialOwners.driverLicense') && (
                <div className="space-y-2">
                  <Label htmlFor={`owners.${index}.driverLicense`}>
                    Driver's License Number {isFieldRequired('beneficialOwners.driverLicense') && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id={`owners.${index}.driverLicense`}
                    {...register(`owners.${index}.driverLicense`)}
                    placeholder="DL123456789"
                  />
                  {errors.owners?.[index]?.driverLicense && (
                    <p className="text-sm text-red-600">{errors.owners[index]?.driverLicense?.message}</p>
                  )}
                </div>
              )}

              {/* Home Address - Conditionally rendered based on field settings */}
              {shouldShowField('beneficialOwners.homeAddress') && (
                <div className="space-y-4 md:col-span-2">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">
                      Home Address {isFieldRequired('beneficialOwners.homeAddress') && <span className="text-red-500">*</span>}
                    </Label>

                    {/* Same as Business Address checkbox */}
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox
                        id={`sameAsBusinessAddress-${index}`}
                        checked={watch(`owners.${index}.sameAsBusinessAddress`) || false}
                        onCheckedChange={(checked) => handleSameAsBusinessAddressChange(index, !!checked)}
                        className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      />
                      <Label htmlFor={`sameAsBusinessAddress-${index}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                        Same as business address
                      </Label>
                    </div>

                    {/* Address input - show when not using business address */}
                    {!watch(`owners.${index}.sameAsBusinessAddress`) && (
                      <AddressAutocomplete
                        label=""
                        placeholder="Enter home address..."
                        required={isFieldRequired('beneficialOwners.homeAddress')}
                        value={watch(`owners.${index}.homeAddress`) || { street: '', city: '', state: '', zipCode: '', fullAddress: '' }}
                        onChange={(address) => handleHomeAddressChange(index, address)}
                        error={(() => {
                          const homeAddressErrors = errors.owners?.[index]?.homeAddress;
                          if (!homeAddressErrors) return undefined;
                          if (typeof homeAddressErrors.message === 'string') return homeAddressErrors.message;
                          const addrErrors = homeAddressErrors as any;
                          return addrErrors.street?.message ||
                                 addrErrors.city?.message ||
                                 addrErrors.state?.message ||
                                 addrErrors.zipCode?.message ||
                                 addrErrors.fullAddress?.message;
                        })()}
                      />
                    )}

                    {/* Show business address when using it */}
                    {watch(`owners.${index}.sameAsBusinessAddress`) && initialData?.corporationInfo?.businessAddress && (
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                        <div className="flex items-center space-x-2 mb-2">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">Using business address:</span>
                        </div>
                        <p className="text-sm text-blue-800 leading-relaxed">
                          {initialData.corporationInfo.businessAddress.fullAddress}
                        </p>
                      </div>
                    )}

                    {/* Hidden inputs for home address form validation */}
                    <input type="hidden" {...register(`owners.${index}.homeAddress.street`)} />
                    <input type="hidden" {...register(`owners.${index}.homeAddress.city`)} />
                    <input type="hidden" {...register(`owners.${index}.homeAddress.state`)} />
                    <input type="hidden" {...register(`owners.${index}.homeAddress.zipCode`)} />
                    <input type="hidden" {...register(`owners.${index}.homeAddress.fullAddress`)} />
                  </div>
                </div>
              )}
            </div>
                </div>
              )}
            </Card>
          );
        })}

        {/* Add Owner Button - Only for Corporation Applications */}
        {applicationType === 'corporation' && fields.length < 4 && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 hover:bg-blue-50 transition-colors">
            <Button
              type="button"
              variant="outline"
              onClick={addOwner}
              className="w-full h-auto py-4 border-0 bg-transparent hover:bg-blue-100 text-gray-700 hover:text-blue-700"
            >
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <UserPlus className="h-5 w-5" />
                </div>
                <div className="text-center">
                  <div className="font-medium">Add Another Beneficial Owner</div>
                  <div className="text-sm text-gray-500">
                    Maximum 4 owners • {4 - fields.length} remaining
                  </div>
                </div>
              </div>
            </Button>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-between pt-6">
          {onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={isSubmitting || (applicationType === 'corporation' && totalOwnership < 75)} 
            className="ml-auto"
          >
            {isSubmitting ? 'Saving...' : 'Save & Continue'}
          </Button>
        </div>
      </CardContent>
    </form>
  );
}
